import React, { useState, useEffect, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import { ColDef } from "ag-grid-community";
import {
  ClientApprovalGridProps,
  GridColumnTitleRenderer,
  parseDeepLinkFromUrl,
  TimeRenderer,
  useDebounce,
} from "./ClientApprovalGrid.utils";
import { fetchApprovalClients } from "./ClientApprovalGrid.service";
import ActionsRenderer from "./components/ActionsRenderer/ActionsRenderer";
import ClientNameRenderer from "./components/ClientNameRenderer/ClientNameRenderer";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";
import RefreshGridButton from "../../../pages/reusable_components/RefreshGridButton/RefreshGridButton";
import styles from "./ClientApprovalGrid.module.css";
import {
  CygnusGridRef,
  dateFilterParams,
} from "../../reusable_components/CygnusGrid/CygnusGrid.utils";
import ClientApprovalWizard from "../ClientApprovalWizard/ClientApprovalWizard";
import { useGlobalStore } from "../../store/globalStore";
import {
  createPartnerProvider,
  createPurposeProvider,
  createUsersProvider,
  createClientTypesProvider,
  createStatusProvider,
  createLabelMap,
} from "../../../pages/reusable_components/CygnusGrid/CygnusGrid.utils";
import { fetchFirmReferenceData } from "../../firm_setup/FieldMappings/FieldMappings.service";

/**
 * ClientApprovalGrid
 *
 * Displays a table of clients awaiting approval, with support for:
 * - Server-side fetching and search
 * - Opening the approval wizard modal
 * - Refreshing the grid
 */
// ClientApprovalGrid.tsx (updated)

const ClientApprovalGrid: React.FC<ClientApprovalGridProps> = ({
  deepLink,
}) => {
  const [approvalModalShow, setApprovalModalShow] = useState<boolean>(false);
  const [clientRecord, setClientRecord] = useState<any>(0);
  const [clientMasterId, setClientMasterId] = useState<number>(0);
  const [firmDetailId, setFirmDetailId] = useState<number>(0);
  const [attestFormId, setAttestFormId] = useState<number>(0);
  const [onboardingFormId, setOnboardingFormId] = useState<number>(0);
  const [taxAcceptanceFormId, setTaxAcceptanceFormId] = useState<number>(0);
  const [auditAcceptanceFormId, setAuditAcceptanceFormId] = useState<number>(0);

  const [refreshApiProgress, setRefreshApiProgress] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>();
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const gridRef = useRef<CygnusGridRef>(null);
  // top of component
  const [gridReady, setGridReady] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [rejectOptions, setRejectOptions] = useState<any[]>([]);

  const debouncedSearchText = useDebounce(searchText, 300);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const { purposeOptions, primaryPartnerOptions, officeLocationOptions, users, clientTypes } =
    useGlobalStore();

  const officeLabelByVal = createLabelMap(
    officeLocationOptions,
    (o) => o.value,
    (o) => o.label
  );
  const partnerLabelByVal = createLabelMap(
    primaryPartnerOptions,
    (o) => o.value,
    (o) => o.label
  );
  const losLabelByVal = createLabelMap(
    purposeOptions,
    (o) => String(o.value ?? o.code),
    (o) => o.label
  );

  const getOfficeLocationValues = createPartnerProvider(officeLocationOptions);
  const getPartnerValues = createPartnerProvider(primaryPartnerOptions);
  const getLosValues = createPurposeProvider(purposeOptions);
  const getUserValues = createUsersProvider(users);
  const getClientTypeValues = createClientTypesProvider(clientTypes);

  const openApprovalForRow = (row: any) => {
    if (!row) return;
    setClientRecord(row);
    setClientMasterId(Number(row.ClientMasterId ?? 0));
    setFirmDetailId(Number(row.FirmDetailId ?? 0));
    setAttestFormId(Number(row.AttestFormId ?? 0));
    setOnboardingFormId(Number(row.OnboardingFormId ?? 0));
    setTaxAcceptanceFormId(Number(row.TaxAcceptanceFormId ?? 0));
    setAuditAcceptanceFormId(Number(row.AuditAcceptanceFormId ?? 0));
    setApprovalModalShow(true);
  };

  useEffect(() => {
    const dl = deepLink ?? parseDeepLinkFromUrl();
    if (!dl?.recordId || dl.action !== "openApproval") return;
    if (!gridReady || !dataLoaded) return;

    const api = gridRef.current?.api;
    if (!api) return;

    const wanted = String(dl.recordId).trim();
    const wantedNum = Number(wanted);

    // 1) We already have all loaded rows — just pick the data object
    const allNodes: any[] = [];
    api.forEachNode((node) => allNodes.push(node.data));
    const dataHit =
      allNodes.find(
        (d) =>
          String(d?.ClientMasterId) === wanted ||
          String(d?.ClientMasterId) === String(wantedNum) ||
          String(d?.ClientId) === wanted ||
          String(d?.ClientId || "").endsWith(wanted)
      ) || null;

    if (dataHit) {
      // 2) Open the modal immediately (no fetch, no extra dance)
      openApprovalForRow(dataHit);

      // 3) (Nice-to-have) also select + scroll the row node if you want
      let nodeMatch: any = null;
      api.forEachNode((node: any) => {
        if (
          node.data?.ClientMasterId === dataHit.ClientMasterId ||
          node.data?.ClientId === dataHit.ClientId
        )
          nodeMatch = node;
      });
      if (nodeMatch) {
        api.ensureNodeVisible(nodeMatch, "middle");
        nodeMatch.setSelected(true);
      }

      // 4) Clean URL so refresh doesn’t re-open
      const url = new URL(window.location.href);
      url.searchParams.delete("recordId");
      url.searchParams.delete("action");
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", url.toString());

      return; // done ✅
    }

    // If somehow not found in cache (unlikely given your log), you could
    // fall back to a filter/paging strategy — but you said no fetch, so we stop here.
  }, [deepLink, gridReady, dataLoaded]);

  useEffect(() => {
    const textParams = { debounceMs: 300, caseSensitive: false };
    const jsonColDefs: ColDef[] = [
      // {
      //   field: "__select",
      //   colId: "select",
      //   headerName: "",
      //   checkboxSelection: true,
      //   headerCheckboxSelection: true,
      //   suppressHeaderMenuButton: true,
      //   sortable: false,
      //   width: 50,
      //   maxWidth: 50,
      //   filter: false,
      //   headerClass: "ag-center-select-header",
      // },
      {
        field: "ClientId",
        colId: "clientId",
        headerName: GridColumnTitleRenderer("Client Id"),
        sortable: false, // disable: backend doesn’t sort
        valueFormatter: (p) =>
          p.value == null || p.value === "" ? "" : p.value,
        // minWidth: 140,
        filter: "agTextColumnFilter",
        filterParams: textParams,
      },
      // in your columnDefs setup, add near the top:
      {
        field: "ClientMasterId",
        colId: "clientMasterId",
        headerName: "ClientMasterId",
        hide: true,
        filter: "agNumberColumnFilter",
      },
      {
        field: "ClientName",
        colId: "clientName",
        headerName: GridColumnTitleRenderer("Client Name"),
        sortable: false,
        valueFormatter: (p) =>
          p.value == null || p.value === "" ? "" : p.value,
        // minWidth: 180,
        filter: "agTextColumnFilter",
        filterParams: textParams,
        cellRenderer: ClientNameRenderer,
        cellRendererParams: (params: any) => ({
          data: params.data,
          setApprovalModalShow: setApprovalModalShow,
          setClientMasterId: setClientMasterId,
          setAttestFormId: setAttestFormId,
          setOnboardingFormId: setOnboardingFormId,
          setTaxAcceptanceFormId: setTaxAcceptanceFormId,
          setAuditAcceptanceFormId: setAuditAcceptanceFormId,
          setClientRecord: setClientRecord,
          setFirmDetailId: setFirmDetailId,
        }),
      },
      {
        field: "ClientTypeName",
        colId: "clientTypeName",
        headerName: GridColumnTitleRenderer("Client Type"),
        sortable: false,
        filter: "agSetColumnFilter",
        filterParams: {
          values: getClientTypeValues,
          refreshValuesOnOpen: true,
        },
      },
      {
        field: "OfficeLocation",
        colId: "officeLocation",
        headerName: "Office Location",
        sortable: false,
        minWidth: 130,
        filter: "agSetColumnFilter",
        filterParams: {
          values: getOfficeLocationValues,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) => officeLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          officeLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        field: "BusinessUnit",
        colId: "lineOfService",
        headerName: GridColumnTitleRenderer("Line of Service"),
        sortable: false,
        minWidth: 140,
        filter: "agSetColumnFilter",
        filterParams: {
          values: getLosValues,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) => losLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          losLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        field: "PrimaryPartner",
        colId: "primaryPartner",
        headerName: GridColumnTitleRenderer("Primary Partner"),
        sortable: false,
        minWidth: 180,
        filter: "agSetColumnFilter",
        filterParams: {
          values: getPartnerValues,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            partnerLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        field: "OriginatingPartner",
        colId: "originatingPartner",
        headerName: GridColumnTitleRenderer("Originating Partner"),
        sortable: false,
        minWidth: 180,
        filter: "agSetColumnFilter",
        filterParams: {
          values: getPartnerValues,
          refreshValuesOnOpen: true,
          valueFormatter: (p: any) =>
            partnerLabelByVal.get(String(p.value)) ?? "",
        },
        valueFormatter: (p) =>
          partnerLabelByVal.get(String(p.value)) ?? p.value ?? "",
      },
      {
        headerName: "Created By",
        field: "CreatedBy",
        colId: "createdBy",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getUserValues,
          refreshValuesOnOpen: true,
        },
      },
      {
        field: "CreatedDate",
        colId: "createdDate",
        headerName: GridColumnTitleRenderer("Created Date"),
        sortable: false,
        filter: false,
        minWidth: 160,
        filterParams: dateFilterParams,
        cellRenderer: TimeRenderer,
        cellRendererParams: (p: any) => ({ value: p.value, showTime: true }),
      },
      {
        field: "UpdatedDate",
        colId: "updatedDate",
        headerName: GridColumnTitleRenderer("Updated Date"),
        sortable: false,
        filter: false,
        filterParams: dateFilterParams,
        cellRenderer: TimeRenderer,
        cellRendererParams: (p: any) => ({ value: p.value, showTime: true }),
      },
      {
        field: "",
        headerName: "Actions",
        cellStyle: { "border-right-color": "#ccc" },
        filter: false,
        pinned: "right",
        suppressHeaderMenuButton: true,
        minWidth: 70,
        maxWidth: 70,
        cellRenderer: ActionsRenderer,
        cellRendererParams: (params: any) => ({
          data: params.data,
          setApprovalModalShow: setApprovalModalShow,
          setClientMasterId: setClientMasterId,
          setAttestFormId: setAttestFormId,
          setOnboardingFormId: setOnboardingFormId,
          setTaxAcceptanceFormId: setTaxAcceptanceFormId,
          setAuditAcceptanceFormId: setAuditAcceptanceFormId,
          setClientRecord: setClientRecord,
          setFirmDetailId: setFirmDetailId,
        }),
      },
    ];
    setColumnDefs(jsonColDefs);
  }, [purposeOptions]);

  const handleRefresh = () => gridRef.current?.refreshGrid();
  
  useEffect(() => {
    async function loadRejectOptions() {
      try {
        const data = await fetchFirmReferenceData("ClientRejectionOptions");
        const values =
          Array.isArray(data)
            ? data
                .map((item) => item?.ReferenceDataValue)
                .filter((v) => v !== undefined && v !== null)
            : [];
        setRejectOptions(values || []);
      } catch (e) {
        console.error("Failed to load Client Rejection Options", e);
      }
    }

    loadRejectOptions();
  }, []);

  return (
    <>
      {approvalModalShow && (
        <ClientApprovalWizard
          show={approvalModalShow}
          onHide={() => setApprovalModalShow(false)}
          clientMasterId={clientMasterId}
          attestFormId={attestFormId}
          onboardingFormId={onboardingFormId}
          taxAcceptanceFormId={taxAcceptanceFormId}
          auditAcceptanceFormId={auditAcceptanceFormId}
          clientRecord={clientRecord}
          firmDetailId={firmDetailId}
          rejectOptions={rejectOptions}
          refresh={handleRefresh}
        />
      )}

      <Row className={styles.clientGridHeader}>
        <Col md={3}>
          <span className={styles.clientTitle}>Approve Clients</span>
        </Col>
        <Col md={9} className={styles.clientsButtonsContainer}>
          {/* <div style={{ padding: "0px 12px" }}>
            <input
              type="text"
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
              className="form-control move-left custom-input"
            />
          </div> */}
          <RefreshGridButton
            refreshGridApiProgress={refreshApiProgress}
            handleRefresh={handleRefresh}
          />
        </Col>
      </Row>

      <Row className={styles.clientGridContainer}>
        <Col md={12}>
          <CygnusGrid
            ref={gridRef}
            columnDefs={columnDefs}
            fetchData={fetchApprovalClients}
            searchText={debouncedSearchText}
            dataIdentifier={"ClientMasterId"}
            rowSelection={"multiple"}
            onLoadingChange={setRefreshApiProgress}
            onSelectionChange={(rows) => setSelectedRows(rows)}
            floatingFilters={true}
            onGridReady={() => {
              console.log("✅ CygnusGrid onGridReady fired (parent)");
              setGridReady(true);
            }}
            onFirstDataRendered={() => {
              console.log("✅ onFirstDataRendered fired (parent)");
              setDataLoaded(true);
            }}
            filterKeyMap={{
              clientMasterId: "ClientMasterId",
              clientId: "ClientId",
              clientName: "ClientName",
              clientEmail: "ClientEmail",
              clientTypeName: "ClientTypeName",
              backgroundCheckStatus: "BackgroundCheckStatus",
              officeLocation: "OfficeLocation",
              lineOfService: "LineOfService",
              primaryPartner: "PrimaryPartner",
              originatingPartner: "OriginatingPartner",
              createdBy: "CreatedBy",
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default ClientApprovalGrid;
